const FIRST_HALF_LABEL = '1st Half';
const SECOND_HALF_LABEL = '2nd Half';

export function getPlayerDisplayName(player) {
  const fullName = [player?.first_name, player?.last_name]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' ');

  return fullName || player?.email || 'Unknown player';
}

export function getCurrentMatchMoment(durationMinutes, timerSeconds, halftimeCompleted = false) {
  const totalMinutes = Math.max(1, Number(durationMinutes) || 0);
  const totalSeconds = totalMinutes * 60;
  const remainingSeconds = Math.max(0, Number(timerSeconds) || 0);
  const elapsedSeconds = Math.max(0, totalSeconds - remainingSeconds);
  const minute = Math.max(1, Math.min(totalMinutes, Math.floor(elapsedSeconds / 60) + 1));

  return {
    minute,
    period: halftimeCompleted ? SECOND_HALF_LABEL : FIRST_HALF_LABEL,
  };
}

export function nextSortIndex(matchEvents) {
  return (Array.isArray(matchEvents) ? matchEvents : []).reduce((max, event) => {
    const current = Number(event?.sortIndex);
    return Number.isFinite(current) ? Math.max(max, current) : max;
  }, -1) + 1;
}

export function createGoalEvent({
  matchEvents,
  player,
  teamIndex,
  durationMinutes,
  timerSeconds,
  halftimeCompleted = false,
}) {
  return {
    type: 'goal',
    sortIndex: nextSortIndex(matchEvents),
    teamIndex,
    playerEmail: player?.email || null,
    playerName: getPlayerDisplayName(player),
    assistEmail: null,
    assistName: '',
    fromTeamIndex: null,
    toTeamIndex: null,
    ...getCurrentMatchMoment(durationMinutes, timerSeconds, halftimeCompleted),
  };
}

export function createCardEvent({
  matchEvents,
  player,
  teamIndex,
  cardType,
  durationMinutes,
  timerSeconds,
  halftimeCompleted = false,
}) {
  return {
    type: cardType === 'red' ? 'red_card' : 'yellow_card',
    sortIndex: nextSortIndex(matchEvents),
    teamIndex,
    playerEmail: player?.email || null,
    playerName: getPlayerDisplayName(player),
    assistEmail: null,
    assistName: '',
    fromTeamIndex: null,
    toTeamIndex: null,
    ...getCurrentMatchMoment(durationMinutes, timerSeconds, halftimeCompleted),
  };
}

export function createTeamSwitchEvents({
  matchEvents,
  switches,
  durationMinutes,
  timerSeconds,
  halftimeCompleted = false,
}) {
  let sortIndex = nextSortIndex(matchEvents);
  const moment = getCurrentMatchMoment(durationMinutes, timerSeconds, halftimeCompleted);

  return (Array.isArray(switches) ? switches : [])
    .filter((entry) => entry?.player && entry.fromTeamIndex !== entry.toTeamIndex)
    .map((entry) => ({
      type: 'team_switch',
      sortIndex: sortIndex++,
      teamIndex: entry.toTeamIndex,
      playerEmail: entry.player?.email || null,
      playerName: getPlayerDisplayName(entry.player),
      assistEmail: null,
      assistName: '',
      fromTeamIndex: entry.fromTeamIndex,
      toTeamIndex: entry.toTeamIndex,
      ...moment,
    }));
}

export function attachAssistToGoal(matchEvents, goalSortIndex, assistPlayer) {
  let attached = false;
  const nextEvents = (Array.isArray(matchEvents) ? matchEvents : []).map((event) => {
    if (!attached && event?.type === 'goal' && event.sortIndex === goalSortIndex) {
      attached = true;
      return {
        ...event,
        assistEmail: assistPlayer?.email || null,
        assistName: getPlayerDisplayName(assistPlayer),
      };
    }

    return event;
  });

  return { attached, matchEvents: nextEvents };
}

export function removeLastGoalEvent(matchEvents, playerEmail, teamIndex) {
  const events = Array.isArray(matchEvents) ? matchEvents : [];

  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (
      event?.type === 'goal' &&
      event.playerEmail === playerEmail &&
      event.teamIndex === teamIndex
    ) {
      return {
        removedEvent: event,
        matchEvents: [...events.slice(0, index), ...events.slice(index + 1)],
      };
    }
  }

  return { removedEvent: null, matchEvents: events };
}

export function removeAssistFromLatestGoal(matchEvents, assistEmail, teamIndex) {
  const events = Array.isArray(matchEvents) ? matchEvents : [];

  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (
      event?.type === 'goal' &&
      event.assistEmail === assistEmail &&
      event.teamIndex === teamIndex
    ) {
      return {
        removedEvent: event,
        matchEvents: [
          ...events.slice(0, index),
          {
            ...event,
            assistEmail: null,
            assistName: '',
          },
          ...events.slice(index + 1),
        ],
      };
    }
  }

  return { removedEvent: null, matchEvents: events };
}

export function removeLastCardEvent(matchEvents, playerEmail, cardType, teamIndex) {
  const targetType = cardType === 'red' ? 'red_card' : 'yellow_card';
  const events = Array.isArray(matchEvents) ? matchEvents : [];

  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (
      event?.type === targetType &&
      event.playerEmail === playerEmail &&
      event.teamIndex === teamIndex
    ) {
      return {
        removedEvent: event,
        matchEvents: [...events.slice(0, index), ...events.slice(index + 1)],
      };
    }
  }

  return { removedEvent: null, matchEvents: events };
}

export function sortMatchEvents(matchEvents) {
  return [...(Array.isArray(matchEvents) ? matchEvents : [])].sort((left, right) => {
    if ((left?.minute || 0) !== (right?.minute || 0)) {
      return (left?.minute || 0) - (right?.minute || 0);
    }

    return (left?.sortIndex || 0) - (right?.sortIndex || 0);
  });
}

export function describeMatchEvent(event) {
  const playerName = event?.playerName || event?.playerEmail || 'Unknown player';

  if (event?.type === 'goal') {
    return event?.assistName
      ? `${playerName} scored, assisted by ${event.assistName}`
      : `${playerName} scored`;
  }

  if (event?.type === 'yellow_card') {
    return `${playerName} received a yellow card`;
  }

  if (event?.type === 'red_card') {
    return `${playerName} received a red card`;
  }

  if (event?.type === 'team_switch') {
    return `${playerName} moved to Team ${Number(event?.toTeamIndex) + 1}`;
  }

  return playerName;
}
